import classNames from 'classnames';
import React, { useState } from 'react';

export interface InputProps {
  inputClassName?: string;
  captionClassName?: string;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  inputProps?: React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  captionProps?: React.DetailedHTMLProps<
    React.LabelHTMLAttributes<HTMLLabelElement>,
    HTMLLabelElement
  >;
  caption?: React.ReactNode;
  status?: 'danger' | 'none';
  placeholder?: string;
  value?: string | number | readonly string[] | undefined;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  onEnter?: (e: boolean) => void;
  field?: 'search' | 'text';
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  onLeftIconClick?: () => void;
  onRightIconClick?: () => void;
  type?: string | undefined;
  disabled?: boolean | undefined;
  label?: string;
  labelClassName?: string;
  required?: boolean;
  textSecClassName?: string;
  onClick?: ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined;
}

export const Input = ({
  inputClassName,
  captionClassName,
  wrapperClassName,
  status,
  caption,
  captionProps,
  inputProps,
  placeholder,
  value,
  onChange,
  onEnter,
  field,
  leftIcon,
  rightIcon,
  onLeftIconClick,
  onRightIconClick,
  type,
  disabled,
  label,
  labelClassName,
  required,
  textSecClassName,
  wrapperStyle,
  onClick,
}: InputProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const wrapperClasses = classNames({
    'border border-gray-200 outline-none rounded-md text-gray-999 min-w-44 justify-between flex relative flex items-center':
      true,
    'border-red-700': status === 'danger',
    'bg-white px-16': field === 'search',
    'bg-gray-100 px-12 py-13': field === 'text',
    [wrapperClassName || '']: !!wrapperClassName,
  });
  const inputClasses = classNames({
    'relative inline-block w-full p-4 outline-none bg-transparent': true,
    'text-gray-600': isFocused,
    'text-gray-400': disabled,
    [inputClassName || '']: !!inputClassName,
  });
  const captionClasses = classNames({
    'mt-8 text-sm': true,
    'text-red-700': status === 'danger',
    [captionClassName || '']: !!captionClassName,
  });
  const leftIconClasses = classNames({
    'mr-8 text-gray-500': true,
    'cursor-pointer': !!onLeftIconClick,
  });
  const rightIconClasses = classNames({
    'ml-8 text-gray-500': true,
    'cursor-pointer': !!onRightIconClick,
  });
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (onEnter) onEnter(true);
    }
    if (inputProps?.onKeyPress) inputProps.onKeyPress(e);
  };
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (inputProps?.onFocus) inputProps.onFocus(e);
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (inputProps?.onBlur) inputProps.onBlur(e);
  };
  const labelClasses = classNames({
    'text-sm text-gray-600 mb-8 block': true,
    [labelClassName || '']: !!labelClassName,
  });
  return (
    <div className={textSecClassName} onClick={onClick}>
      {label && (
        <label className={labelClasses}>
          {label}
          {required && <span className="text-red-800">*</span>}
        </label>
      )}
      <div className={wrapperClasses} style={wrapperStyle}>
        {leftIcon ? (
          <div className={leftIconClasses} onClick={onLeftIconClick}>
            {leftIcon}
          </div>
        ) : null}
        <div className="flex-auto">
          <input
            className={inputClasses}
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            aria-label={placeholder}
            {...inputProps}
            onKeyPress={handleKeyPress}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
          />
        </div>
        {rightIcon ? (
          <div className={rightIconClasses} onClick={onRightIconClick}>
            {rightIcon}
          </div>
        ) : null}
      </div>
      {caption ? (
        <label className={captionClasses} {...captionProps}>
          {caption}
        </label>
      ) : null}
    </div>
  );
};
